import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, RollbackOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  financeApi,
  type IncomePaymentMethod,
  type OtherIncome,
  type OtherIncomeInput,
} from '@/api/finance';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const METHOD_LABEL: Record<IncomePaymentMethod, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
  ONLINE_TRANSFER: 'Online transfer',
};

interface FormShape {
  incomeDate: Dayjs;
  incomeCategoryId: number;
  amount: number;
  description: string;
  paymentMethod: IncomePaymentMethod;
  bankAccountId: number;
  glAccountId: number;
  reference?: string;
}

export function FinanceOtherIncomePage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FINANCE:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FINANCE:EDIT'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [adding, setAdding] = useState(false);
  const [reversing, setReversing] = useState<OtherIncome | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [form] = Form.useForm<FormShape>();

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['finance', 'other-income', params],
    queryFn: () => financeApi.listOtherIncome(params.from, params.to),
  });

  const { data: bankAccounts } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
    staleTime: 60_000,
  });

  const { data: accounts } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: financeApi.listAccounts,
    staleTime: 60_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['setup', 'lookups', 'income-categories', 'active'],
    queryFn: () => setupApi.listLookup('income-categories', true),
    staleTime: 5 * 60_000,
  });

  const add = useMutation({
    mutationFn: (values: FormShape) => {
      const body: OtherIncomeInput = {
        incomeDate: values.incomeDate.format('YYYY-MM-DD'),
        incomeCategoryId: values.incomeCategoryId,
        amount: values.amount.toFixed(2),
        description: values.description.trim(),
        paymentMethod: values.paymentMethod,
        bankAccountId: values.bankAccountId,
        glAccountId: values.glAccountId,
        reference: values.reference?.trim() || undefined,
      };
      return financeApi.addOtherIncome(body);
    },
    onSuccess: () => {
      message.success('Income recorded and posted to ledger');
      qc.invalidateQueries({ queryKey: ['finance'] });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
      setAdding(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      financeApi.reverseOtherIncome(id, reason),
    onSuccess: () => {
      message.success('Income entry reversed');
      qc.invalidateQueries({ queryKey: ['finance'] });
      setReversing(null);
      setReverseReason('');
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({
      incomeDate: dayjs(),
      paymentMethod: 'CASH',
    });
    setAdding(true);
  };

  const watchedMethod = Form.useWatch('paymentMethod', form);
  const bankOptions = (bankAccounts ?? [])
    .filter((b) => (watchedMethod === 'CASH' ? b.cash : !b.cash))
    .map((b) => ({ value: b.id, label: b.accountName + (b.bankName ? ` · ${b.bankName}` : '') }));

  const incomeAccountOptions = (accounts ?? [])
    .filter((a) => a.accountType === 'INCOME' && a.postingAccount && a.active)
    .map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }));

  const totalAmount = (data ?? [])
    .filter((e) => !e.reversed)
    .reduce((acc, e) => acc + Number(e.amount), 0);

  const columns: TableProps<OtherIncome>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'incomeDate',
      width: 130,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Category',
      dataIndex: 'incomeCategoryName',
      width: 180,
      render: (n: string | null) => n ?? '—',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (d: string, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text delete={row.reversed}>{d}</Typography.Text>
          {row.glAccountName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              GL: {row.glAccountName}
            </Typography.Text>
          )}
          {row.reference && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Ref: {row.reference}
            </Typography.Text>
          )}
          {row.reversed && (
            <Tag color="default">Reversed{row.reversalReason ? ` · ${row.reversalReason}` : ''}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Method',
      width: 160,
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Tag color={row.paymentMethod === 'CASH' ? 'success' : 'processing'}>
            {METHOD_LABEL[row.paymentMethod]}
          </Tag>
          {row.bankAccountName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.bankAccountName}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 140,
      align: 'right',
      render: (a: string, row) => (
        <Typography.Text strong delete={row.reversed}>PKR {PKR(a)}</Typography.Text>
      ),
    },
    {
      title: 'Actions',
      width: 130,
      align: 'right',
      render: (_, row) =>
        canEdit && !row.reversed ? (
          <Button
            size="small"
            type="text"
            icon={<RollbackOutlined />}
            onClick={() => setReversing(row)}
          >
            Reverse
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Other income"
        description="Non-fee income such as donations, grants, premises rent. Auto-posts to the ledger."
        count={data ? { value: data.length, label: 'in range' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              New income
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {data && data.length > 0 && (
          <Tag color="success" style={{ fontSize: 13 }}>
            Total · PKR {PKR(totalAmount)}
          </Tag>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No income entries in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data ?? []}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </Card>
      )}

      <Modal
        title="Record income"
        open={adding}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={add.isPending}
        okText="Record"
        destroyOnHidden
        width={560}
      >
        {adding && (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed) => {
              if (changed.paymentMethod !== undefined) form.setFieldValue('bankAccountId', undefined);
            }}
            onFinish={(values) => add.mutate(values)}
          >
            <Form.Item label="Date" name="incomeDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item label="Category" name="incomeCategoryId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
            <Form.Item label="Description" name="description" rules={[{ required: true, max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
            <Form.Item label="Amount (PKR)" name="amount" rules={[{ required: true, type: 'number', min: 0.01 }]}>
              <InputNumber style={{ width: '100%' }} step={100} min={0} />
            </Form.Item>
            <Form.Item label="GL income account" name="glAccountId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={incomeAccountOptions}
                placeholder="Pick an INCOME posting account"
              />
            </Form.Item>
            <Form.Item label="Method" name="paymentMethod" rules={[{ required: true }]}>
              <Select
                options={(Object.keys(METHOD_LABEL) as IncomePaymentMethod[]).map((m) => ({
                  value: m,
                  label: METHOD_LABEL[m],
                }))}
              />
            </Form.Item>
            <Form.Item label="Receive into" name="bankAccountId" rules={[{ required: true }]}>
              <Select options={bankOptions} placeholder="Pick an account" />
            </Form.Item>
            <Form.Item label="Reference" name="reference" rules={[{ max: 200 }]}>
              <Input placeholder="Donor name, txn id, etc. (optional)" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={reversing ? `Reverse income · PKR ${PKR(reversing.amount)}` : 'Reverse income'}
        open={reversing != null}
        onCancel={() => {
          setReversing(null);
          setReverseReason('');
        }}
        okText="Reverse"
        okButtonProps={{ danger: true, disabled: !reverseReason.trim() || reverse.isPending }}
        confirmLoading={reverse.isPending}
        onOk={() => reversing && reverse.mutate({ id: reversing.id, reason: reverseReason.trim() })}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          The original ledger entries are preserved. A compensating pair is posted to neutralize the impact.
        </Typography.Paragraph>
        <label style={{ fontWeight: 500 }}>Reason (required)</label>
        <Input.TextArea
          autoFocus
          autoSize={{ minRows: 2, maxRows: 4 }}
          value={reverseReason}
          onChange={(e) => setReverseReason(e.target.value)}
          style={{ marginTop: 6 }}
        />
      </Modal>
    </div>
  );
}
